import {
  pgTable,
  text,
  serial,
  integer,
  boolean,
  timestamp,
  decimal,
  varchar,
  index,
  date,
} from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// ── Lead statuses (admin-configurable) ──────────────────────────────────────
// Pipeline-style status flow that admins can rename, reorder, and recolor.
// Built-ins seeded by the migration: new_lead, contacted, appointment_set,
// qualified, disqualified. Slugs (`name`) are immutable on built-ins; labels
// can still be edited.
export const leadStatuses = pgTable(
  "lead_statuses",
  {
    id: serial("id").primaryKey(),
    name: varchar("name", { length: 50 }).notNull().unique(),    // slug — referenced from admin_leads.status
    label: varchar("label", { length: 100 }).notNull(),
    color: varchar("color", { length: 30 }).notNull().default("slate"), // Tailwind palette key (slate, blue, emerald, amber, red, etc.)
    sortOrder: integer("sort_order").notNull().default(0),
    isDefault: boolean("is_default").notNull().default(false),    // exactly one row is the default for new leads
    isWon: boolean("is_won").notNull().default(false),
    isLost: boolean("is_lost").notNull().default(false),
    isBuiltIn: boolean("is_built_in").notNull().default(false),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (t) => ({
    sortOrderIdx: index("idx_lead_statuses_sort_order").on(t.sortOrder),
  })
);

export const insertLeadStatusSchema = createInsertSchema(leadStatuses)
  .omit({ id: true, createdAt: true, updatedAt: true })
  .extend({
    name: z.string().min(1).max(50).regex(/^[a-z0-9_]+$/, "Slug must be lowercase letters, numbers, underscores"),
    label: z.string().min(1).max(100),
  });

export type LeadStatus = typeof leadStatuses.$inferSelect;
export type InsertLeadStatus = z.infer<typeof insertLeadStatusSchema>;

// ── Opportunity stages (admin-configurable) ─────────────────────────────────
// Same shape as lead statuses plus a win-probability used for pipeline-value
// forecasting. Built-ins: discovery (10%), qualification (25%), proposal_sent
// (50%), negotiation (70%), closed_won (100%, isWon), closed_lost (0%, isLost).
export const opportunityStages = pgTable(
  "opportunity_stages",
  {
    id: serial("id").primaryKey(),
    name: varchar("name", { length: 50 }).notNull().unique(),
    label: varchar("label", { length: 100 }).notNull(),
    color: varchar("color", { length: 30 }).notNull().default("slate"),
    sortOrder: integer("sort_order").notNull().default(0),
    isDefault: boolean("is_default").notNull().default(false),
    isWon: boolean("is_won").notNull().default(false),
    isLost: boolean("is_lost").notNull().default(false),
    isBuiltIn: boolean("is_built_in").notNull().default(false),
    winProbability: decimal("win_probability", { precision: 5, scale: 2 }).default("0"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (t) => ({
    sortOrderIdx: index("idx_opportunity_stages_sort_order").on(t.sortOrder),
  })
);

export const insertOpportunityStageSchema = createInsertSchema(opportunityStages)
  .omit({ id: true, createdAt: true, updatedAt: true })
  .extend({
    name: z.string().min(1).max(50).regex(/^[a-z0-9_]+$/, "Slug must be lowercase letters, numbers, underscores"),
    label: z.string().min(1).max(100),
  });

export type OpportunityStage = typeof opportunityStages.$inferSelect;
export type InsertOpportunityStage = z.infer<typeof insertOpportunityStageSchema>;

// ── Admin leads ─────────────────────────────────────────────────────────────
// Early-funnel contacts being worked toward becoming opportunities.
// `status` references leadStatuses.name (loose reference — not a hard FK so
// renaming/deleting a status doesn't cascade-break leads; UI displays the raw
// value if the status row is missing).
export const adminLeads = pgTable(
  "admin_leads",
  {
    id: serial("id").primaryKey(),
    firstName: text("first_name").notNull(),
    lastName: text("last_name").notNull(),
    company: text("company"),
    email: text("email"),
    phone: text("phone"),
    source: varchar("source", { length: 50 }).notNull().default("manual"), // manual | web | web_form | referral | event | cold_outreach | gohighlevel
    sourceExternalId: varchar("source_external_id", { length: 255 }),       // dedup key for webhook sources
    status: varchar("status", { length: 50 }).notNull().default("new_lead"),
    assignedTo: varchar("assigned_to", { length: 255 }),                    // users.id
    convertedToOpportunity: boolean("converted_to_opportunity").notNull().default(false),
    opportunityId: integer("opportunity_id"),                                // FK → opportunities.id; nullable
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (t) => ({
    statusIdx: index("idx_admin_leads_status").on(t.status),
    assignedIdx: index("idx_admin_leads_assigned").on(t.assignedTo),
    sourceIdx: index("idx_admin_leads_source").on(t.source),
    sourceDedupIdx: index("idx_admin_leads_source_dedup").on(t.source, t.sourceExternalId),
    convertedIdx: index("idx_admin_leads_converted").on(t.convertedToOpportunity),
  })
);

export const insertAdminLeadSchema = createInsertSchema(adminLeads)
  .omit({ id: true, createdAt: true, updatedAt: true, convertedToOpportunity: true, opportunityId: true })
  .extend({
    firstName: z.string().min(1).max(100),
    lastName: z.string().min(1).max(100),
    email: z.string().email().optional().nullable(),
  });

export type AdminLead = typeof adminLeads.$inferSelect;
export type InsertAdminLead = z.infer<typeof insertAdminLeadSchema>;

// ── Lead notes ──────────────────────────────────────────────────────────────
// Same shape as client_notes. Kept on the lead row after conversion (the
// opportunity links back via opportunities.lead_id; spec 09 update says
// don't deep-copy).
export const adminLeadNotes = pgTable(
  "admin_lead_notes",
  {
    id: serial("id").primaryKey(),
    leadId: integer("lead_id").notNull(),
    title: varchar("title", { length: 200 }),
    body: text("body").notNull(),
    createdBy: varchar("created_by", { length: 255 }).notNull(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (t) => ({
    leadIdx: index("idx_admin_lead_notes_lead_id").on(t.leadId),
    leadCreatedIdx: index("idx_admin_lead_notes_lead_created").on(t.leadId, t.createdAt),
  })
);

export const insertAdminLeadNoteSchema = createInsertSchema(adminLeadNotes)
  .omit({ id: true, createdAt: true, updatedAt: true })
  .extend({
    title: z.string().max(200).optional().nullable(),
    body: z.string().min(1, "Note body is required"),
  });

export type AdminLeadNote = typeof adminLeadNotes.$inferSelect;
export type InsertAdminLeadNote = z.infer<typeof insertAdminLeadNoteSchema>;

// ── Opportunities ───────────────────────────────────────────────────────────
export const opportunities = pgTable(
  "opportunities",
  {
    id: serial("id").primaryKey(),
    name: text("name").notNull(),                  // typically contact name
    company: text("company"),
    email: text("email"),
    phone: text("phone"),
    stage: varchar("stage", { length: 50 }).notNull().default("discovery"),
    potentialValue: decimal("potential_value", { precision: 12, scale: 2 }),
    projectedCloseDate: date("projected_close_date"),
    source: varchar("source", { length: 50 }),
    assignedTo: varchar("assigned_to", { length: 255 }),  // users.id
    leadId: integer("lead_id"),                     // FK → admin_leads.id; nullable
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (t) => ({
    stageIdx: index("idx_opportunities_stage").on(t.stage),
    assignedIdx: index("idx_opportunities_assigned").on(t.assignedTo),
    leadIdx: index("idx_opportunities_lead").on(t.leadId),
  })
);

export const insertOpportunitySchema = createInsertSchema(opportunities)
  .omit({ id: true, createdAt: true, updatedAt: true })
  .extend({
    name: z.string().min(1).max(255),
    email: z.string().email().optional().nullable(),
    potentialValue: z
      .union([z.string(), z.number()])
      .optional()
      .nullable()
      .transform((v) => (v === undefined || v === null ? null : v.toString())),
  });

export type Opportunity = typeof opportunities.$inferSelect;
export type InsertOpportunity = z.infer<typeof insertOpportunitySchema>;

// ── Opportunity stage history ───────────────────────────────────────────────
// Append-only audit trail of kanban moves. Powers the Activity tab on the
// opportunity record.
export const opportunityStageHistory = pgTable(
  "opportunity_stage_history",
  {
    id: serial("id").primaryKey(),
    opportunityId: integer("opportunity_id").notNull(),
    fromStage: varchar("from_stage", { length: 50 }),  // null on first stage assignment
    toStage: varchar("to_stage", { length: 50 }).notNull(),
    changedBy: varchar("changed_by", { length: 255 }).notNull(),
    changedAt: timestamp("changed_at").defaultNow().notNull(),
  },
  (t) => ({
    opportunityIdx: index("idx_opportunity_stage_history_opp").on(t.opportunityId, t.changedAt),
  })
);

export type OpportunityStageHistory = typeof opportunityStageHistory.$inferSelect;
